import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TrendComment } from '../../services/trends.service';

@Component({
  selector: 'app-trend-comment-thread',
  templateUrl: './trend-comment-thread.component.html',
  styleUrls: ['./trend-comment-thread.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule]
})
export class TrendCommentThreadComponent implements OnDestroy {
  @Input() comments: TrendComment[] = [];
  @Input() comment!: TrendComment;
  @Input() postId!: number;
  @Input() replyTarget: { postId: number; comment: TrendComment } | null = null;
  @Input() replyText = '';
  @Input() replySubmitting = false;
  @Input() variant: 'social' | 'review' = 'social';
  @Output() replyRequested = new EventEmitter<TrendComment>();
  @Output() replyTextChange = new EventEmitter<string>();
  @Output() replySubmitted = new EventEmitter<void>();
  @Output() replyCancelled = new EventEmitter<void>();
  @Output() reactionRequested = new EventEmitter<{ comment: TrendComment; reaction: string }>();

  expanded = false;
  reactionMenuOpen = false;
  private pressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressTriggered = false;

  get children(): TrendComment[] {
    return this.comment.id
      ? this.comments.filter(item => item.parentId === this.comment.id)
      : [];
  }

  get isReplying(): boolean {
    return this.replyTarget?.postId === this.postId && this.replyTarget.comment === this.comment;
  }

  initials(name: string): string {
    return (name || 'משתמש').trim().split(/\s+/).slice(0, 2)
      .map(part => part[0]?.toUpperCase() || '').join('');
  }

  reactionCount(key: string): number {
    return this.comment.reactionCounts?.[key] || 0;
  }

  totalReactions(): number {
    return Object.values(this.comment.reactionCounts || {}).reduce((total, count) => total + count, 0);
  }

  reactionEmoji(key: string | null): string {
    return ({ like: '👍', rocket: '🚀', fire: '🔥', ice: '❄️', giggles: '😆' } as Record<string, string>)[key || 'like'] || '👍';
  }

  startReactionPress(event: Event): void {
    event.stopPropagation();
    this.longPressTriggered = false;
    this.clearPressTimer();
    this.pressTimer = setTimeout(() => {
      this.longPressTriggered = true;
      this.reactionMenuOpen = true;
    }, 500);
  }

  finishReactionPress(event: Event): void {
    event.stopPropagation();
    this.clearPressTimer();
    if (!this.longPressTriggered) {
      this.reactionRequested.emit({ comment: this.comment, reaction: 'like' });
    }
  }

  cancelReactionPress(): void {
    this.clearPressTimer();
  }

  chooseReaction(event: Event, reaction: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.reactionMenuOpen = false;
    this.reactionRequested.emit({ comment: this.comment, reaction });
  }

  @HostListener('document:pointerdown')
  closeReactionMenu(): void {
    this.reactionMenuOpen = false;
  }

  ngOnDestroy(): void {
    this.clearPressTimer();
  }

  private clearPressTimer(): void {
    if (this.pressTimer) clearTimeout(this.pressTimer);
    this.pressTimer = null;
  }
}
